// src/components/Map/FieldMap.jsx
import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';

import { calculateArea } from '../../utils/geoUtils';
import { getNdviColor, getNdviDescription, ndviColorRamp } from '../../utils/ndviColors';
import { getNdmiColor, getNdmiDescription, ndmiColorRamp } from '../../utils/ndmiColors';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import './FieldMap.css';
import MapTimeline from './MapTimeline';
import NdviLegend from './NdviLegend';
import { userService } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { isWeatherCardStation, groupMarker } from './weatherStationCard';
import { groupTitle } from '../../utils/fieldclimateGroups';

// window.L and leaflet-draw moved to inside component to ensure order

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const FieldMap = ({
  fields,
  selectedField,
  onFieldSelect,
  onFieldCreate,
  onFieldUpdate,
  onFieldDelete,
  overlayData,
  timelineData,
  onTimelineSelect,
  activeIndex = 'NDVI',
  setActiveIndex,
  sensors = [],
  onSensorClick,
  selectedSensor = null,
  selectedSensorId = null,
  isAddingSensor = false,
  onSensorMove,
  readOnly = false,
  onSensorAdd,
  isAdmin = false,
  irrigationOn = false // Show water flow animation on selected field
}) => {
  // Refs
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const sensorLayerRef = useRef(null);
  const isInitializedRef = useRef(false);
  const searchMarkerRef = useRef(null);
  const minMaxMarkersRef = useRef(null);
  const irrigationLayerRef = useRef(null);
  const initTimeoutRef = useRef(null); // Ref for init timeout
  const isMountedRef = useRef(true); // Track mount status
  const lastFitFieldIdRef = useRef(null); // Track which field we last fitted bounds to
  const searchActiveRef = useRef(false); // True after a search, prevents auto fitBounds

  // State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingField, setPendingField] = useState(null);
  const [formData, setFormData] = useState({ name: '', cropType: '', userId: '', plantingDate: new Date().toISOString().split('T')[0] }); // Add userId
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]); // Users state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({ id: null, name: '', cropType: '', plantingDate: '', userId: '', elevation: '', kc: '' });

  const { t, language } = useLanguage();
  const currentLocale = language === 'en' ? 'en-US' : 'tr-TR';

  const editingLayerRef = useRef(null); // Store layer being edited
  const isEditingPolygonRef = useRef(false); // True while draw-edit toolbar is active

  // Callback refs to avoid stale closures
  const onFieldCreateRef = useRef(onFieldCreate);
  const onFieldUpdateRef = useRef(onFieldUpdate);
  const onFieldDeleteRef = useRef(onFieldDelete);
  const fieldsRef = useRef(fields);

  const handleEditEntry = (field) => {
    setEditFormData({
      id: field.id,
      name: field.name || '',
      cropType: field.cropType || '',
      plantingDate: field.plantingDate ? new Date(field.plantingDate).toISOString().split('T')[0] : '',
      userId: field.userId || '',
      elevation: field.elevation != null ? field.elevation : '',
      kc: field.kc != null ? field.kc : ''
    });
    setShowEditModal(true);
  };

  const handleSaveUpdate = async () => {
    if (!editFormData.name) {
      alert('Tarla adı boş olamaz.');
      return;
    }

    try {
      if (onFieldUpdateRef.current) {
        await onFieldUpdateRef.current(editFormData.id, {
          name: editFormData.name,
          cropType: editFormData.cropType,
          plantingDate: editFormData.plantingDate,
          userId: editFormData.userId ? parseInt(editFormData.userId) : null,
          elevation: editFormData.elevation !== '' ? parseFloat(editFormData.elevation) : null,
          kc: editFormData.kc !== '' ? parseFloat(editFormData.kc) : null
        });
        setShowEditModal(false);
        alert('Tarla bilgileri güncellendi.');
      }
    } catch (error) {
      console.error('Update error:', error);
      alert('Güncelleme sırasında hata oluştu.');
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    onFieldCreateRef.current = onFieldCreate;
    onFieldUpdateRef.current = onFieldUpdate;
    onFieldDeleteRef.current = onFieldDelete;
    fieldsRef.current = fields;

    return () => {
      isMountedRef.current = false;
    };
  }, [onFieldCreate, onFieldUpdate, onFieldDelete, fields]);

  // Fetch users if Admin
  useEffect(() => {
    if (isAdmin) {
      const fetchUsers = async () => {
        try {
          const userList = await userService.getAllUsers();
          setUsers(userList);
        } catch (error) {
          console.error('Error fetching users:', error);
        }
      };
      fetchUsers();
    }
  }, [isAdmin]);

  // Helper: parse DMS (e.g., 36°31'13.6"N)
  const parseDMS = (dms) => {
    const regex = /([0-9.]+)[°\s]*([0-9.]+)?['\s]*([0-9.]+)?["\s]*([NSEW])/i;
    const match = dms.match(regex);
    if (!match) return null;
    const deg = parseFloat(match[1]);
    const min = match[2] ? parseFloat(match[2]) : 0;
    const sec = match[3] ? parseFloat(match[3]) : 0;
    const dir = match[4].toUpperCase();
    let decimal = deg + min / 60 + sec / 3600;
    if (dir === 'S' || dir === 'W') decimal = -decimal;
    return decimal;
  };

  // Search handler (supports decimal and DMS)
  const handleSearch = async () => {
    if (!searchQuery) return;
    try {
      // Try Nominatim (decimal or place name)
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}`);
      const results = await response.json();
      if (results && results.length > 0) {
        const { lat, lon } = results[0];
        const latNum = parseFloat(lat);
        const lonNum = parseFloat(lon);
        if (mapRef.current) {
          searchActiveRef.current = true;
          mapRef.current.setView([latNum, lonNum], 20);
          if (searchMarkerRef.current) mapRef.current.removeLayer(searchMarkerRef.current);
          const marker = L.marker([latNum, lonNum]);
          marker.bindPopup(`${latNum.toFixed(6)}, ${lonNum.toFixed(6)}`).openPopup();
          marker.addTo(mapRef.current);
          searchMarkerRef.current = marker;
        }
        return;
      }
      // DMS fallback
      const parts = searchQuery.split(/[ ,]+/);
      if (parts.length >= 2) {
        const latDMS = parseDMS(parts[0]);
        const lonDMS = parseDMS(parts[1]);
        if (latDMS !== null && lonDMS !== null && mapRef.current) {
          searchActiveRef.current = true;
          mapRef.current.setView([latDMS, lonDMS], 20);
          if (searchMarkerRef.current) mapRef.current.removeLayer(searchMarkerRef.current);
          const marker = L.marker([latDMS, lonDMS]);
          marker.bindPopup(`${latDMS.toFixed(6)}, ${lonDMS.toFixed(6)}`).openPopup();
          marker.addTo(mapRef.current);
          searchMarkerRef.current = marker;
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Initialize map
  useEffect(() => {
    if (isInitializedRef.current || !mapContainerRef.current) return;
    try {
      // Ensure L is global for plugins
      if (!window.L) window.L = L;

      // Check if leaflet-draw is loaded, if not try to load it
      if (!L.Control.Draw) {
        console.warn('Leaflet Draw not loaded, attempting require...');
        require('leaflet-draw');
      }

      const map = L.map(mapContainerRef.current, {
        center: [38.8153, 30.5281],
        zoom: 13,
        zoomControl: true,
        maxZoom: 24, // Added maxZoom
      });
      mapRef.current = map;
      // Satellite layer
      L.tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}', {
        maxZoom: 24,
        maxNativeZoom: 20,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
      }).addTo(map);
      // Draw layer
      const drawnItems = new L.FeatureGroup();
      map.addLayer(drawnItems);
      drawnItemsRef.current = drawnItems;

      // Sensor layer - CRITICAL: Initialize and add to map
      const sensorLayer = new L.FeatureGroup();
      map.addLayer(sensorLayer);
      sensorLayerRef.current = sensorLayer;
      console.log('📡 Sensor layer initialized and added to map');

      if (!readOnly && isAdmin) {
        const drawControl = new L.Control.Draw({
          position: 'topright',
          draw: {
            polygon: { allowIntersection: false, showArea: true, shapeOptions: { color: '#3388ff', weight: 3, fillOpacity: 0.3 } },
            rectangle: { showArea: true, shapeOptions: { color: '#3388ff', weight: 3, fillOpacity: 0.3 } },
            polyline: false,
            circle: false,
            circlemarker: false,
            marker: false,
          },
          edit: {
            featureGroup: drawnItems, // Use the proper featureGroup reference
            remove: false // Disable delete via toolbar (we have a custom delete button)
          }
        });
        map.addControl(drawControl);
      }

      // Sensor control - only for ADMIN users
      if (isAdmin) {
        const SensorControl = L.Control.extend({
          options: { position: 'topright' },
          onAdd: function () {
            const container = L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-sensor');
            const button = L.DomUtil.create('a', 'sensor-add-btn', container);
            button.innerHTML = '📡';
            button.href = '#';
            button.title = 'Sensör Ekle';
            button.style.fontSize = '18px';
            button.style.display = 'flex';
            button.style.alignItems = 'center';
            button.style.justifyContent = 'center';
            L.DomEvent.on(button, 'click', (e) => {
              L.DomEvent.stopPropagation(e);
              L.DomEvent.preventDefault(e);
              document.dispatchEvent(new CustomEvent('trigger-add-sensor'));
            });
            return container;
          },
        });
        map.addControl(new SensorControl());
      }




      // Let's just revert the specific block I added in the previous step


      // Draw created handler (unchanged)
      map.on(L.Draw.Event.CREATED, (event) => {
        try {
          const layer = event.layer;
          const geoJSON = layer.toGeoJSON();
          drawnItems.addLayer(layer);
          const area = calculateArea(geoJSON.geometry.coordinates);
          const fieldData = {
            area: typeof area === 'number' ? Number(area.toFixed(2)) : 0,
            geometry: { type: 'Polygon', coordinates: geoJSON.geometry.coordinates },
          };
          setPendingField(fieldData);
          setFormData({
            name: `Field ${(fieldsRef.current?.length || 0) + 1}`,
            cropType: '',
            userId: '',
            plantingDate: new Date().toISOString().split('T')[0]
          });
          setIsModalOpen(true);
        } catch (err) {
          console.error('Drawing error:', err);
          alert('Çizim sırasında hata oluştu: ' + err.message);
        }
      });

      // Edit & Delete handlers
      map.on(L.Draw.Event.EDITED, (event) => {
        try {
          event.layers.eachLayer((layer) => {
            if (layer.fieldId && onFieldUpdateRef.current) {
              const geoJSON = layer.toGeoJSON();
              const newArea = calculateArea(geoJSON.geometry.coordinates);
              onFieldUpdateRef.current(layer.fieldId, { geometry: geoJSON.geometry, area: Number(newArea.toFixed(2)) });
            }
          });
          console.log('✅ Edit saved successfully');
        } catch (error) {
          console.error('Error during save:', error);
        }
      });

      map.on(L.Draw.Event.EDITSTART, () => {
        isEditingPolygonRef.current = true;
      });

      map.on(L.Draw.Event.EDITSTOP, () => {
        isEditingPolygonRef.current = false;
      });

      map.on(L.Draw.Event.DELETED, (event) => {
        event.layers.eachLayer((layer) => {
          if (layer.fieldId && onFieldDeleteRef.current) {
            onFieldDeleteRef.current(layer.fieldId);
          }
        });
      });

      isInitializedRef.current = true;

      // Safe invalidateSize
      initTimeoutRef.current = setTimeout(() => {
        if (isMountedRef.current && mapRef.current && mapContainerRef.current) {
          map.invalidateSize();
        }
      }, 100);
    } catch (error) {
      console.error('Map initialization error:', error);
    }
    return () => {
      if (initTimeoutRef.current) clearTimeout(initTimeoutRef.current);
      if (mapRef.current) {
        try {
          mapRef.current.remove();
        } catch (e) {
          console.warn('Map removal error:', e);
        }
        mapRef.current = null;
        isInitializedRef.current = false;
      }
    };
  }, [isAdmin, readOnly]);



  // Resize map on field change
  useEffect(() => {
    const resizeMap = () => {
      if (isMountedRef.current && mapRef.current && mapContainerRef.current) {
        mapRef.current.invalidateSize();
      }
    };
    const timer = setTimeout(resizeMap, 150);
    return () => clearTimeout(timer);
  }, [fields]);

  // Render fields and overlays (unchanged logic, only shortened for brevity)
  useEffect(() => {
    if (!mapRef.current || !drawnItemsRef.current) return;
    // Don't redraw while polygon edit toolbar is active — it would destroy editable layers
    if (isEditingPolygonRef.current) return;
    const drawnItems = drawnItemsRef.current;
    drawnItems.clearLayers();
    fields.forEach((field) => {
      if (!field.geometry?.coordinates) return;
      const coordinates = field.geometry.coordinates[0];
      const latLngs = coordinates.map((c) => [c[1], c[0]]);
      const isSelected = selectedField?.id === field.id;
      let fillColor = isSelected ? '#ff7800' : '#3388ff';
      let fillOpacity = isSelected ? 0.3 : 0.2;
      let weight = isSelected ? 3 : 2;
      let color = isSelected ? '#ffffff' : '#3388ff';
      const activeImagePath = activeIndex === 'NDVI' ? overlayData?.ndviImagePath :
        activeIndex === 'NDRE' ? overlayData?.ndreImagePath :
          overlayData?.ndmiImagePath;

      // Debug logging
      if (isSelected && overlayData) {
        console.log('🔍 Active Index:', activeIndex);
        console.log('📊 Overlay Data:', overlayData);
        console.log('🖼️ Active Image Path:', activeImagePath);
      }

      if (isSelected && overlayData && activeImagePath) {
        fillColor = overlayData.color;
        fillOpacity = 0.15;
      }
      const polygon = L.polygon(latLngs, { color, weight, fillColor, fillOpacity, className: isSelected && activeImagePath ? 'field-with-ndvi' : '' });
      polygon.fieldId = field.id;

      // Editing handler will be attached after adding to map
      let popupContent = ''; // Restored line
      if (!isAddingSensor) {
        const getHealthStatus = (value) => {
          const description = activeIndex === 'NDMI' ? getNdmiDescription(value, t) : getNdviDescription(value, t);
          const color = activeIndex === 'NDMI' ? getNdmiColor(value) : getNdviColor(value);
          let icon = '⚪';

          if (activeIndex === 'NDMI') {
            if (value >= 0.3) icon = '💧'; // Good moisture
            else if (value >= -0.1) icon = '⛅'; // Normal/Medium
            else icon = '🏜️'; // Dry
          } else {
            if (value >= 0.6) icon = '✅';
            else if (value >= 0.4) icon = '⚠️';
            else icon = '❌';
          }

          return { text: description, color, icon };
        };
        const cropLabel = field.cropType || '—';
        const plantDate = field.plantingDate
          ? new Date(field.plantingDate).toLocaleDateString(currentLocale, { day: '2-digit', month: 'short', year: 'numeric' })
          : '—';

        popupContent = `
          <div class="fp-card">
            <div class="fp-header">
              <div class="fp-header-left">
                <span class="fp-field-name">${field.name}</span>
                <span class="fp-crop-badge">${cropLabel}</span>
              </div>
            </div>
            <div class="fp-meta-row">
              <div class="fp-meta-item">
                <span class="fp-meta-label">${t('field.area')}</span>
                <span class="fp-meta-value">${field.area} ha</span>
              </div>
              <div class="fp-meta-divider"></div>
              <div class="fp-meta-item">
                <span class="fp-meta-label">${t('field.planting_date')}</span>
                <span class="fp-meta-value">${plantDate}</span>
              </div>
            </div>`;

        if (isSelected && overlayData) {
          const activeValue = activeIndex === 'NDVI' ? overlayData.ndvi :
            activeIndex === 'NDRE' ? overlayData.ndre :
              overlayData.ndmi;
          const minVal = (activeIndex === 'NDVI' ? overlayData.minNdvi :
            activeIndex === 'NDRE' ? overlayData.minNdre :
              overlayData.minNdmi)?.toFixed(3) || '—';
          const maxVal = (activeIndex === 'NDVI' ? overlayData.maxNdvi :
            activeIndex === 'NDRE' ? overlayData.maxNdre :
              overlayData.maxNdmi)?.toFixed(3) || '—';
          const health = getHealthStatus(activeValue);
          const analysisDate = new Date(overlayData.date).toLocaleDateString(currentLocale, { day: '2-digit', month: 'short', year: 'numeric' });

          popupContent += `
            <div class="fp-index-section">
              <div class="fp-index-header">
                <span class="fp-index-label">${activeIndex} ${t('map.analysis')}</span>
                <span class="fp-index-date">${analysisDate}</span>
              </div>
              <div class="fp-index-body">
                <div class="fp-index-main">
                  <span class="fp-index-num" style="color:${health.color}">${activeValue?.toFixed(3) || '—'}</span>
                  <span class="fp-index-status" style="color:${health.color}">${health.text}</span>
                </div>
                <div class="fp-index-range">
                  <span class="fp-range-item"><span class="fp-range-lbl">MIN</span><span class="fp-range-val">${minVal}</span></span>
                  <span class="fp-range-sep"></span>
                  <span class="fp-range-item"><span class="fp-range-lbl">MAX</span><span class="fp-range-val">${maxVal}</span></span>
                </div>
              </div>
              <div class="fp-bar-track"><div class="fp-bar-fill" style="width:${Math.max(0, Math.min(100, ((activeValue + 1) / 2) * 100)).toFixed(1)}%;background:${health.color}"></div></div>
            </div>`;
        }

        popupContent += `
            ${isAdmin ? `<div class="fp-actions"><button class="fp-edit-btn" id="edit-field-${field.id}">Edit Field</button></div>` : ''}
          </div>`;
      }
      polygon.bindPopup(popupContent, { maxWidth: 300 });

      polygon.on('popupopen', () => {
        const editBtn = document.getElementById(`edit-field-${field.id}`);
        if (editBtn) {
          editBtn.onclick = (e) => {
            e.stopPropagation();
            handleEditEntry(field);
          };
        }
      });

      polygon.on('click', (e) => {
        if (isAddingSensor) {
          L.DomEvent.stopPropagation(e);
          if (onSensorAdd) onSensorAdd(e.latlng.lat, e.latlng.lng, field.id);
        } else if (isEditingPolygonRef.current || (polygon.editing && polygon.editing.enabled())) {
          // Suppress field selection while polygon edit mode is active
          L.DomEvent.stopPropagation(e);
        } else {
          if (onFieldSelect) onFieldSelect(field);
        }
      });

      // Add polygon to map
      drawnItems.addLayer(polygon);
      // Image overlay if selected
      if (isSelected && overlayData && activeImagePath) {
        const bounds = L.latLngBounds(latLngs);
        const imageOverlay = L.imageOverlay(activeImagePath, bounds, { opacity: 0.8, interactive: true, className: 'ndvi-image-overlay' });
        if (!isAddingSensor) imageOverlay.bindPopup(popupContent);
        imageOverlay.on('click', (e) => {
          if (isAddingSensor && onSensorAdd) onSensorAdd(e.latlng.lat, e.latlng.lng, field.id);
        });
        drawnItems.addLayer(imageOverlay);
      }
    });
    // Fit bounds — only when selectedField actually changes, never after a search
    if (selectedField?.geometry) {
      const selectedFieldChanged = lastFitFieldIdRef.current !== selectedField.id;
      if (selectedFieldChanged) {
        lastFitFieldIdRef.current = selectedField.id;
        searchActiveRef.current = false; // Reset search lock when user picks a field
        if (searchMarkerRef.current) {
          mapRef.current.removeLayer(searchMarkerRef.current);
          searchMarkerRef.current = null;
        }
        const coords = selectedField.geometry.coordinates[0];
        const latLngs = coords.map((c) => [c[1], c[0]]);
        if (latLngs.length > 0) {
          mapRef.current.fitBounds(L.latLngBounds(latLngs), { padding: [50, 50] });
        }
      }
    } else if (fields.length > 0 && !searchActiveRef.current && lastFitFieldIdRef.current === null) {
      // Only do the all-fields overview on very first load, never after a search
      const allBounds = [];
      fields.forEach((f) => {
        if (f.geometry?.coordinates) {
          f.geometry.coordinates[0].forEach((c) => allBounds.push([c[1], c[0]]));
        }
      });
      if (allBounds.length > 0) {
        lastFitFieldIdRef.current = 'all';
        mapRef.current.fitBounds(L.latLngBounds(allBounds), { padding: [50, 50] });
      }
    }
  }, [fields, selectedField, onFieldSelect, overlayData, activeIndex, isAddingSensor, onSensorAdd, t, currentLocale]);

  // Sensor click handling (unchanged)
  useEffect(() => {
    if (!mapRef.current) return;
    const onMapClick = (e) => {
      if (isAddingSensor && onSensorAdd) {
        let clickedFieldId = null;
        if (drawnItemsRef.current) {
          drawnItemsRef.current.eachLayer((layer) => {
            if (layer instanceof L.Polygon && layer.fieldId) {
              if (layer.getBounds().contains(e.latlng)) clickedFieldId = layer.fieldId;
            }
          });
        }
        onSensorAdd(e.latlng.lat, e.latlng.lng, clickedFieldId);
      }
    };
    mapRef.current.on('click', onMapClick);
    mapRef.current.getContainer().style.cursor = isAddingSensor ? 'crosshair' : '';
    return () => {
      if (mapRef.current) mapRef.current.off('click', onMapClick);
    };
  }, [isAddingSensor, onSensorAdd]);

  // Render sensors (unchanged)
  useEffect(() => {
    console.log('📡 Sensor rendering effect triggered. Sensors:', sensors?.length, 'Map:', !!mapRef.current, 'Layer:', !!sensorLayerRef.current);
    if (!mapRef.current || !sensorLayerRef.current) return;
    const sensorLayer = sensorLayerRef.current;
    sensorLayer.clearLayers();

    const getSensorIcon = (type, isSelected) => {
      const configs = {
        Temperature:     { bg: '#ef4444', border: '#b91c1c', svg: '<path d="M12 2a3 3 0 0 0-3 3v7.27A6 6 0 1 0 15 12.27V5a3 3 0 0 0-3-3zm0 2a1 1 0 0 1 1 1v1h-2V5a1 1 0 0 1 1-1zm0 14a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-6a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" fill="white"/>' },
        Humidity:        { bg: '#3b82f6', border: '#1d4ed8', svg: '<path d="M12 2C8 8 5 12 5 15a7 7 0 0 0 14 0c0-3-3-7-7-13zm0 18a5 5 0 0 1-5-5c0-2 1.8-5 5-9.5C15.2 10 17 13 17 15a5 5 0 0 1-5 5z" fill="white"/>' },
        Moisture:        { bg: '#10b981', border: '#047857', svg: '<path d="M12 2a1 1 0 0 0-.707.293l-7 7A7 7 0 0 0 12 21a7 7 0 0 0 7.707-11.707l-7-7A1 1 0 0 0 12 2zm0 2.414 6.293 6.293A5 5 0 0 1 7.222 17.1 5 5 0 0 1 6.707 10.707z" fill="white"/><circle cx="12" cy="16" r="2" fill="white"/>' },
        SoilTemperature: { bg: '#a16207', border: '#713f12', svg: '<path d="M17 8C8 10 5.9 16.17 3.82 19.07L5.71 21 7 19.07C8 20 9 21 10 21h4c1 0 2-1 3-2l1.29 1.93 1.89-1.93C18.1 16.17 16 10 17 8zm-5 9a3 3 0 0 1-3-3c0-1.5 1-3 3-5 2 2 3 3.5 3 5a3 3 0 0 1-3 3z" fill="white"/>' },
        Rainfall:        { bg: '#6366f1', border: '#4338ca', svg: '<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9v-2h2v2zm0-4H9V8h2v4zm4 4h-2v-2h2v2zm0-4h-2V8h2v4z" fill="white"/><path d="M8 7l1.5 3M11 7l1.5 3M14 7l1.5 3" stroke="white" stroke-width="1.5" stroke-linecap="round"/>' },
        WindSpeed:       { bg: '#64748b', border: '#334155', svg: '<path d="M3 8h10a3 3 0 0 0 0-6 3 3 0 0 0-2.83 4H3v2zm14-2a1 1 0 1 1 0 2H14.17A5 5 0 0 1 17 6zM3 12h14a3 3 0 0 0 0-6 3 3 0 0 0-2.17 5H3v1zm14-2a1 1 0 1 1 0 2H14.83A5 5 0 0 1 17 10zM3 16h8a3 3 0 0 0 0-6 3 3 0 0 0-2.83 4H3v2zm8-2a1 1 0 1 1 0 2h-1.17A5 5 0 0 1 11 14z" fill="white"/>' },
        Multi:           { bg: '#8b5cf6', border: '#6d28d9', svg: '<circle cx="8" cy="8" r="3" fill="white" opacity="0.9"/><circle cx="16" cy="8" r="3" fill="white" opacity="0.9"/><circle cx="8" cy="16" r="3" fill="white" opacity="0.9"/><circle cx="16" cy="16" r="3" fill="white" opacity="0.9"/>' },
      };
      const cfg = configs[type] || { bg: '#475569', border: '#1e293b', svg: '<path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="white"/>' };
      const size = isSelected ? 22 : 16;
      const iconSize = isSelected ? 12 : 9;
      const ringStyle = isSelected
        ? `box-shadow:0 0 0 3px #fff,0 0 0 5px ${cfg.bg},0 4px 12px rgba(0,0,0,0.5);`
        : `box-shadow:0 2px 6px rgba(0,0,0,0.4);`;
      return L.divIcon({
        className: 'sensor-type-icon',
        html: `<div style="
          width:${size}px;height:${size}px;border-radius:50%;
          background:${cfg.bg};border:2px solid ${isSelected ? '#fff' : cfg.border};
          ${ringStyle}
          display:flex;align-items:center;justify-content:center;
          cursor:pointer;transition:all 0.15s;">
          <svg viewBox="0 0 24 24" width="${iconSize}" height="${iconSize}" xmlns="http://www.w3.org/2000/svg">${cfg.svg}</svg>
        </div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    };

    sensors?.forEach((sensor) => {
      if (sensor.lat == null || sensor.lng == null) return;
      const resolvedSelectedId = selectedSensorId ?? selectedSensor?.id ?? null;
      const isSelected = resolvedSelectedId != null && (resolvedSelectedId === sensor.id || resolvedSelectedId === sensor.sensorId);
      if (isWeatherCardStation(sensor)) {
        ['weather', 'agro'].forEach((group) => {
          const groupSelected = isSelected && selectedSensor?.fcGroup === group;
          const { size, iconAnchor, html } = groupMarker(group, groupSelected);
          const groupMarkerLayer = L.marker([sensor.lat, sensor.lng], {
            icon: L.divIcon({ className: 'sensor-type-icon', html, iconSize: [size, size], iconAnchor }),
            zIndexOffset: 1000,
            draggable: isAdmin,
            title: groupTitle(group, language === 'en' ? 'en' : 'tr'),
          });
          groupMarkerLayer.bindTooltip(groupTitle(group, language === 'en' ? 'en' : 'tr'), { direction: 'top', offset: [0, -size / 2] });
          groupMarkerLayer.on('click', () => onSensorClick && onSensorClick({ ...sensor, fcGroup: group }));
          // Both icons show one physical station: dragging either moves the station, and both
          // follow. Dropped outside every field, the station keeps its current field.
          groupMarkerLayer.on('dragend', (e) => {
            const { lat, lng } = e.target.getLatLng();
            let newFieldId = null;
            drawnItemsRef.current?.eachLayer((layer) => {
              if (layer instanceof L.Polygon && layer.fieldId && layer.getBounds().contains([lat, lng])) newFieldId = layer.fieldId;
            });
            onSensorMove && onSensorMove(sensor.sensorId, lat, lng, newFieldId ?? sensor.fieldId, sensor.fieldId);
          });
          sensorLayer.addLayer(groupMarkerLayer);
        });
        return;
      }
      const sensorIcon = getSensorIcon(sensor.type, isSelected);
      const marker = L.marker([sensor.lat, sensor.lng], { icon: sensorIcon, draggable: isAdmin, zIndexOffset: isSelected ? 1000 : 0 });

      const typeLabel = {
        Temperature: 'Air Temperature', Humidity: 'Humidity', Moisture: 'Soil Moisture',
        SoilTemperature: 'Soil Temperature', Rainfall: 'Rainfall', WindSpeed: 'Wind Speed', Multi: 'Multi-Sensor'
      }[sensor.type] || sensor.type;
      const typeColor = {
        Temperature: '#ef4444', Humidity: '#3b82f6', Moisture: '#10b981',
        SoilTemperature: '#a16207', Rainfall: '#6366f1', WindSpeed: '#64748b', Multi: '#8b5cf6'
      }[sensor.type] || '#475569';
      const latestVal = sensor.latestData?.havaSicaklik ?? sensor.latestData?.havaNem ??
        sensor.latestData?.toprakNem ?? sensor.latestData?.toprakSicaklik ??
        sensor.latestData?.Temperature ?? sensor.latestData?.pH ??
        sensor.latestData?.Conductivity ?? null;
      const unit = sensor.type === 'Temperature' || sensor.type === 'SoilTemperature' ? '°C' :
        sensor.type === 'Rainfall' ? 'mm' : sensor.type === 'WindSpeed' ? 'm/s' : '%';

      // Find field name from fields prop
      const fieldName = (fields || []).find(f => f.id === sensor.fieldId)?.name || null;

      const valueBlock = latestVal != null
        ? `<div style="display:flex;align-items:baseline;gap:4px;margin-bottom:2px;">
             <span style="font-family:'IBM Plex Mono',monospace;font-size:22px;font-weight:600;color:#0f172a;line-height:1;font-variant-numeric:tabular-nums;">${latestVal.toFixed(1)}</span>
             <span style="font-size:11px;font-weight:600;color:#94a3b8;">${unit}</span>
           </div>`
        : `<div style="font-size:12px;color:#94a3b8;font-weight:500;margin-bottom:2px;">No data yet</div>`;

      const popupContent = `
        <div style="font-family:'Inter',system-ui,sans-serif;width:210px;overflow:hidden;border-radius:10px;">
          <div style="height:3px;background:${typeColor};"></div>
          <div style="padding:10px 12px 6px;">
            <div style="font-size:10px;font-weight:700;color:${typeColor};text-transform:uppercase;letter-spacing:0.08em;margin-bottom:3px;">${typeLabel}</div>
            <div style="font-weight:700;font-size:13px;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-bottom:1px;">${sensor.name || 'Sensor'}</div>
            <div style="font-size:10px;color:#94a3b8;">ID #${sensor.sensorId}${fieldName ? ' · ' + fieldName : ''}</div>
          </div>
          <div style="padding:8px 12px 4px;border-top:1px solid #f1f5f9;">
            ${valueBlock}
          </div>
          <div style="padding:8px 12px 10px;">
            <button id="sensor-btn-${sensor.sensorId}" style="display:block;width:100%;padding:7px 0;background:#4f46e5;color:#fff;border:none;border-radius:6px;cursor:pointer;font-weight:600;font-size:11px;letter-spacing:0.03em;box-sizing:border-box;font-family:'Inter',sans-serif;">View Details</button>
          </div>
        </div>`;
      marker.bindPopup(popupContent, { minWidth: 210, maxWidth: 210 });
      marker.on('popupopen', () => {
        const btn = document.getElementById(`sensor-btn-${sensor.sensorId}`);
        if (btn) btn.addEventListener('click', () => onSensorClick && onSensorClick(sensor));
      });
      marker.on('dragend', (e) => {
        const { lat, lng } = e.target.getLatLng();
        let newFieldId = null;
        if (drawnItemsRef.current) {
          drawnItemsRef.current.eachLayer((layer) => {
            if (layer instanceof L.Polygon && layer.fieldId) {
              if (layer.getBounds().contains([lat, lng])) newFieldId = layer.fieldId;
            }
          });
        }
        onSensorMove && onSensorMove(sensor.sensorId, lat, lng, newFieldId, sensor.fieldId);
      });
      sensorLayer.addLayer(marker);
    });
  }, [sensors, onSensorClick, onSensorMove, selectedSensorId, selectedSensor, language, isAdmin]);

  // Water flow animation clipped exactly to the selected field polygon
  useEffect(() => {
    if (!mapRef.current) return;

    // Clean up previous layer
    if (irrigationLayerRef.current) {
      mapRef.current.removeLayer(irrigationLayerRef.current);
      irrigationLayerRef.current = null;
    }

    if (!irrigationOn || !selectedField?.geometry?.coordinates) return;

    const map = mapRef.current;
    const coords = selectedField.geometry.coordinates[0];
    const latLngs = coords.map(c => L.latLng(c[1], c[0]));

    // Convert geo coords to pixel points
    const toPixel = (ll) => map.latLngToLayerPoint(ll);
    const pts = latLngs.map(toPixel);

    if (pts.length < 3) return;

    // Bounding box in pixel space
    const xs = pts.map(p => p.x);
    const ys = pts.map(p => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const W = maxX - minX;
    const H = maxY - minY;

    // Build clipPath polygon points (relative to SVG origin = minX,minY)
    const clipPts = pts.map(p => `${p.x - minX},${p.y - minY}`).join(' ');

    const NS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('width', W + 'px');
    svg.setAttribute('height', H + 'px');
    svg.style.overflow = 'hidden';
    svg.style.display = 'block';

    const defs = document.createElementNS(NS, 'defs');
    const clip = document.createElementNS(NS, 'clipPath');
    clip.setAttribute('id', 'field-clip');
    const clipPoly = document.createElementNS(NS, 'polygon');
    clipPoly.setAttribute('points', clipPts);
    clip.appendChild(clipPoly);
    defs.appendChild(clip);
    svg.appendChild(defs);

    const g = document.createElementNS(NS, 'g');
    g.setAttribute('clip-path', 'url(#field-clip)');

    // Background fill (light blue tint over field)
    const bg = document.createElementNS(NS, 'rect');
    bg.setAttribute('x', '0'); bg.setAttribute('y', '0');
    bg.setAttribute('width', W); bg.setAttribute('height', H);
    bg.setAttribute('fill', 'rgba(14,165,233,0.12)');
    g.appendChild(bg);

    // Animated wave stripes — horizontal lines moving downward
    for (let i = 0; i < 6; i++) {
      const wave = document.createElementNS(NS, 'path');
      const y0 = (H / 5) * i;
      // sinusoidal wave path across full width
      const steps = 12;
      const amp = H * 0.06;
      let d = `M -${W * 0.1} ${y0}`;
      for (let s = 0; s <= steps; s++) {
        const x = -W * 0.1 + (W * 1.2 / steps) * s;
        const y = y0 + Math.sin((s / steps) * Math.PI * 2) * amp;
        d += ` L ${x} ${y}`;
      }
      d += ` L ${W * 1.1} ${H} L -${W * 0.1} ${H} Z`;
      wave.setAttribute('d', d);
      wave.setAttribute('fill', i % 2 === 0 ? 'rgba(14,165,233,0.15)' : 'rgba(56,189,248,0.10)');

      // Animate: shift downward continuously
      const anim = document.createElementNS(NS, 'animateTransform');
      anim.setAttribute('attributeName', 'transform');
      anim.setAttribute('type', 'translate');
      anim.setAttribute('from', `0 ${-H / 5}`);
      anim.setAttribute('to', `0 ${H / 5}`);
      anim.setAttribute('dur', `${2 + i * 0.4}s`);
      anim.setAttribute('repeatCount', 'indefinite');
      anim.setAttribute('begin', `${-i * 0.5}s`);
      wave.appendChild(anim);
      g.appendChild(wave);
    }

    // Falling water drops inside polygon
    for (let i = 0; i < 8; i++) {
      const cx = minX < 0 ? 10 + i * (W / 8) : 10 + i * (W / 8);
      const drop = document.createElementNS(NS, 'ellipse');
      drop.setAttribute('cx', cx);
      drop.setAttribute('cy', '0');
      drop.setAttribute('rx', '3');
      drop.setAttribute('ry', '5');
      drop.setAttribute('fill', 'rgba(14,165,233,0.6)');

      const animY = document.createElementNS(NS, 'animate');
      animY.setAttribute('attributeName', 'cy');
      animY.setAttribute('from', `-${H * 0.1}`);
      animY.setAttribute('to', `${H * 1.1}`);
      animY.setAttribute('dur', `${1.2 + (i % 4) * 0.35}s`);
      animY.setAttribute('repeatCount', 'indefinite');
      animY.setAttribute('begin', `${i * 0.22}s`);

      const animO = document.createElementNS(NS, 'animate');
      animO.setAttribute('attributeName', 'opacity');
      animO.setAttribute('values', '0;0.8;0.8;0');
      animO.setAttribute('dur', `${1.2 + (i % 4) * 0.35}s`);
      animO.setAttribute('repeatCount', 'indefinite');
      animO.setAttribute('begin', `${i * 0.22}s`);

      drop.appendChild(animY);
      drop.appendChild(animO);
      g.appendChild(drop);
    }

    svg.appendChild(g);

    // Place SVG at the correct pixel position via a custom Leaflet layer
    const IrrigationLayer = L.Layer.extend({
      onAdd(map) {
        this._map = map;
        const pane = map.getPane('overlayPane');
        pane.appendChild(svg);
        this._update();
        map.on('zoom viewreset moveend', this._update, this);
      },
      onRemove(map) {
        if (svg.parentNode) svg.parentNode.removeChild(svg);
        map.off('zoom viewreset moveend', this._update, this);
      },
      _update() {
        // Recalculate position on every map move/zoom
        const newPts = latLngs.map(ll => map.latLngToLayerPoint(ll));
        const nxs = newPts.map(p => p.x);
        const nys = newPts.map(p => p.y);
        const nMinX = Math.min(...nxs), nMinY = Math.min(...nys);
        const nMaxX = Math.max(...nxs), nMaxY = Math.max(...nys);
        const nW = nMaxX - nMinX;
        const nH = nMaxY - nMinY;

        svg.style.position = 'absolute';
        svg.style.left = nMinX + 'px';
        svg.style.top = nMinY + 'px';
        svg.setAttribute('width', nW + 'px');
        svg.setAttribute('height', nH + 'px');

        // Update clip polygon
        const newClipPts = newPts.map(p => `${p.x - nMinX},${p.y - nMinY}`).join(' ');
        clipPoly.setAttribute('points', newClipPts);
        bg.setAttribute('width', nW);
        bg.setAttribute('height', nH);
      }
    });

    const layer = new IrrigationLayer();
    layer.addTo(map);
    irrigationLayerRef.current = layer;

    return () => {
      if (irrigationLayerRef.current && mapRef.current) {
        mapRef.current.removeLayer(irrigationLayerRef.current);
        irrigationLayerRef.current = null;
      }
    };
  }, [irrigationOn, selectedField]);

  // Hover tooltip with min/max markers
  useEffect(() => {
    const activeImagePath = activeIndex === 'NDVI' ? overlayData?.ndviImagePath :
      activeIndex === 'NDRE' ? overlayData?.ndreImagePath :
        overlayData?.ndmiImagePath;
    if (!mapRef.current || !activeImagePath || !selectedField) {
      // Clear markers when no overlay
      if (minMaxMarkersRef.current && drawnItemsRef.current) {
        minMaxMarkersRef.current.forEach(marker => drawnItemsRef.current.removeLayer(marker));
        minMaxMarkersRef.current = null;
      }
      return;
    }

    const map = mapRef.current;
    const hoverTooltip = L.tooltip({ permanent: false, direction: 'top', className: 'ndvi-hover-tooltip', opacity: 0.9 });
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.src = activeImagePath;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      // Calculate min/max after image loads
      findMinMaxLocations();
    };
    const handleMouseMove = (e) => {
      if (!img.complete) return;
      const latlng = e.latlng;
      const bounds = L.latLngBounds(selectedField.geometry.coordinates[0].map((c) => [c[1], c[0]]));
      if (bounds.contains(latlng)) {
        const nw = bounds.getNorthWest();
        const se = bounds.getSouthEast();
        const xPercent = (latlng.lng - nw.lng) / (se.lng - nw.lng);
        const yPercent = (nw.lat - latlng.lat) / (nw.lat - se.lat);
        const x = Math.floor(xPercent * canvas.width);
        const y = Math.floor(yPercent * canvas.height);
        if (x >= 0 && x < canvas.width && y >= 0 && y < canvas.height) {
          const pixel = ctx.getImageData(x, y, 1, 1).data;
          const [r, g, b] = pixel;

          // Skip transparent pixels (alpha < 128)
          if (pixel[3] < 128) {
            map.removeLayer(hoverTooltip);
            return;
          }

          const hexToRgb = (hex) => {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? [
              parseInt(result[1], 16),
              parseInt(result[2], 16),
              parseInt(result[3], 16)
            ] : [0, 0, 0];
          };

          const activeColorRamp = activeIndex === 'NDMI' ? ndmiColorRamp : ndviColorRamp;
          const valueKey = activeIndex === 'NDMI' ? 'ndmi' : 'ndvi';

          const rgbToIndexValue = (r, g, b, ramp, key) => {
            let matches = ramp.map((item, idx) => {
              const [pr, pg, pb] = hexToRgb(item.color);
              const dist = Math.sqrt(Math.pow(r - pr, 2) + Math.pow(g - pg, 2) + Math.pow(b - pb, 2));
              return { idx, dist, val: item[key] };
            });
            matches.sort((a, b) => a.dist - b.dist);

            const m1 = matches[0];
            const m2 = matches[1];

            if (m1.dist === 0) return m1.val;

            // If the two closest nodes are very far from each other in value space (e.g. jump from green to blue)
            // but the pixel is very close to the first one, just return the first one.
            if (Math.abs(m1.val - m2.val) > 0.4 && m1.dist < 20) return m1.val;

            const totalDist = m1.dist + m2.dist;
            const w1 = 1 - (m1.dist / totalDist);
            const w2 = 1 - (m2.dist / totalDist);
            return (m1.val * w1 + m2.val * w2) / (w1 + w2);
          };

          const indexValue = rgbToIndexValue(r, g, b, activeColorRamp, valueKey);
          const label = activeIndex === 'NDMI' ? getNdmiDescription(indexValue, t) : getNdviDescription(indexValue, t);

          hoverTooltip.setLatLng(latlng)
            .setContent(`<span>${t('map.instant_value')} ${activeIndex}: <strong>${indexValue.toFixed(3)}</strong><br/><small>(${label})</small></span>`)
            .addTo(map);
        }
      } else {
        map.removeLayer(hoverTooltip);
      }
    };
    map.on('mousemove', handleMouseMove);

    // Find and mark min/max NDVI locations
    const findMinMaxLocations = () => {
      if (!img.complete) {
        setTimeout(findMinMaxLocations, 500);
        return;
      }

      const activeColorRamp = activeIndex === 'NDMI' ? ndmiColorRamp : ndviColorRamp;
      const valueKey = activeIndex === 'NDMI' ? 'ndmi' : 'ndvi';

      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16)
        ] : [0, 0, 0];
      };

      const rgbToIndexValue = (r, g, b, ramp, key) => {
        let matches = ramp.map((item, idx) => {
          const [pr, pg, pb] = hexToRgb(item.color);
          const dist = Math.sqrt(Math.pow(r - pr, 2) + Math.pow(g - pg, 2) + Math.pow(b - pb, 2));
          return { idx, dist, val: item[key] };
        });
        matches.sort((a, b) => a.dist - b.dist);

        const m1 = matches[0];
        const m2 = matches[1];

        if (m1.dist === 0) return m1.val;
        if (Math.abs(m1.val - m2.val) > 0.4 && m1.dist < 20) return m1.val;

        const totalDist = m1.dist + m2.dist;
        const w1 = totalDist > 0 ? 1 - (m1.dist / totalDist) : 1;
        const w2 = totalDist > 0 ? 1 - (m2.dist / totalDist) : 0;
        return (m1.val * w1 + m2.val * w2) / (w1 + w2);
      };

      let minNdvi = Infinity;
      let maxNdvi = -Infinity;
      let minLocation = null;
      let maxLocation = null;

      const bounds = L.latLngBounds(selectedField.geometry.coordinates[0].map((c) => [c[1], c[0]]));
      const nw = bounds.getNorthWest();
      const se = bounds.getSouthEast();

      // Sample every 2nd pixel (balanced speed vs accuracy)
      const step = 2;
      console.log('🔍 Scanning image...', canvas.width, 'x', canvas.height, '=', Math.floor(canvas.width / step) * Math.floor(canvas.height / step), 'pixels');
      const startTime = performance.now();

      for (let y = 0; y < canvas.height; y += step) {
        for (let x = 0; x < canvas.width; x += step) {
          const pixel = ctx.getImageData(x, y, 1, 1).data;

          // Skip transparent pixels
          if (pixel[3] < 128) continue;

          const [r, g, b] = pixel;
          const ndviValue = rgbToIndexValue(r, g, b, activeColorRamp, valueKey);

          if (ndviValue < minNdvi) {
            minNdvi = ndviValue;
            const xPercent = x / canvas.width;
            const yPercent = y / canvas.height;
            const lng = nw.lng + xPercent * (se.lng - nw.lng);
            const lat = nw.lat - yPercent * (nw.lat - se.lat);
            minLocation = [lat, lng];
          }

          if (ndviValue > maxNdvi) {
            maxNdvi = ndviValue;
            const xPercent = x / canvas.width;
            const yPercent = y / canvas.height;
            const lng = nw.lng + xPercent * (se.lng - nw.lng);
            const lat = nw.lat - yPercent * (nw.lat - se.lat);
            maxLocation = [lat, lng];
          }
        }
      }

      const endTime = performance.now();
      console.log('⏱️ Scan completed in', (endTime - startTime).toFixed(0), 'ms');
      console.log('📊 Frontend found NDVI range:', minNdvi.toFixed(3), '-', maxNdvi.toFixed(3));

      // Use BACKEND values (more accurate) but frontend coordinates
      const backendMinNdvi = activeIndex === 'NDVI' ? overlayData.minNdvi :
        activeIndex === 'NDRE' ? overlayData.minNdre :
          overlayData.minNdmi;
      const backendMaxNdvi = activeIndex === 'NDVI' ? overlayData.maxNdvi :
        activeIndex === 'NDRE' ? overlayData.maxNdre :
          overlayData.maxNdmi;

      console.log('📊 Backend actual NDVI range:', backendMinNdvi.toFixed(3), '-', backendMaxNdvi.toFixed(3));

      // Use backend values for display
      const displayMinNdvi = backendMinNdvi || minNdvi;
      const displayMaxNdvi = backendMaxNdvi || maxNdvi;

      // Remove old markers
      if (minMaxMarkersRef.current) {
        minMaxMarkersRef.current.forEach(marker => {
          if (drawnItemsRef.current) {
            drawnItemsRef.current.removeLayer(marker);
          }
        });
      }
      minMaxMarkersRef.current = [];

      const drawnItems = drawnItemsRef.current;
      if (!drawnItems) return;

      // Add min marker (red) - using backend value
      if (minLocation) {
        const minIcon = L.divIcon({
          className: 'ndvi-extreme-marker',
          html: `<div style="background: #ef4444; color: white; padding: 8px 12px; border-radius: 20px; font-weight: bold; box-shadow: 0 2px 8px rgba(0,0,0,0.3); white-space: nowrap;">
            📉 MIN: ${displayMinNdvi.toFixed(3)}
          </div>`,
          iconSize: [120, 40],
          iconAnchor: [60, 40]
        });
        const minMarker = L.marker(minLocation, { icon: minIcon });
        const minDescription = activeIndex === 'NDMI' ? getNdmiDescription(displayMinNdvi) : getNdviDescription(displayMinNdvi);
        minMarker.bindPopup(`<strong>En Düşük ${activeIndex}</strong><br/>Değer: ${displayMinNdvi.toFixed(3)}<br/>Durum: ${minDescription}<br/><small>Backend verisi</small>`);
        drawnItems.addLayer(minMarker);
        minMaxMarkersRef.current.push(minMarker);
      }

      // Add max marker (green) - using backend value
      if (maxLocation) {
        const maxIcon = L.divIcon({
          className: 'ndvi-extreme-marker',
          html: `<div style="background: #10b981; color: white; padding: 8px 12px; border-radius: 20px; font-weight: bold; box-shadow: 0 2px 8px rgba(0,0,0,0.3); white-space: nowrap;">
            📈 MAX: ${displayMaxNdvi.toFixed(3)}
          </div>`,
          iconSize: [120, 40],
          iconAnchor: [60, 40]
        });
        const maxMarker = L.marker(maxLocation, { icon: maxIcon });
        const maxDescription = activeIndex === 'NDMI' ? getNdmiDescription(displayMaxNdvi) : getNdviDescription(displayMaxNdvi);
        maxMarker.bindPopup(`<strong>En Yüksek ${activeIndex}</strong><br/>Değer: ${displayMaxNdvi.toFixed(3)}<br/>Durum: ${maxDescription}<br/><small>Backend verisi</small>`);
        drawnItems.addLayer(maxMarker);
        minMaxMarkersRef.current.push(maxMarker);
      }

      console.log('📊 Min/Max NDVI markers placed:', { minNdvi: displayMinNdvi, maxNdvi: displayMaxNdvi });
    };

    findMinMaxLocations();

    return () => {
      map.off('mousemove', handleMouseMove);
      map.removeLayer(hoverTooltip);
      if (minMaxMarkersRef.current && drawnItemsRef.current) {
        minMaxMarkersRef.current.forEach(marker => drawnItemsRef.current.removeLayer(marker));
      }
    };
  }, [overlayData, selectedField, activeIndex, t]);

  return (
    <div className="field-map-container">
      <div ref={mapContainerRef} className="field-map"></div>
      {/* Search overlay */}
      <div className="map-search-overlay">
        <div className="map-search-wrapper">
          <input
            type="text"
            placeholder="Search location..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
          />
          <button onClick={handleSearch}>🔍</button>
        </div>
      </div>
      {/* Index selector */}
      {overlayData && (
        <div className="map-index-selector">
          <button className={activeIndex === 'NDVI' ? 'active' : ''} onClick={() => setActiveIndex('NDVI')}>NDVI</button>
          <button className={activeIndex === 'NDRE' ? 'active' : ''} onClick={() => setActiveIndex('NDRE')}>NDRE</button>
          <button className={activeIndex === 'NDMI' ? 'active' : ''} onClick={() => setActiveIndex('NDMI')}>NDMI</button>
        </div>
      )}
      {/* Timeline Component */}
      <MapTimeline
        data={timelineData}
        onSelect={onTimelineSelect}
        selectedDate={overlayData?.date}
        activeIndex={activeIndex}
      />

      {/* Legend Component */}
      <NdviLegend activeIndex={activeIndex} />

      {/* Create Modal */}
      {isModalOpen && (
        <div className="field-modal-overlay">
          <div className="field-modal">
            <h3>{t('fieldMap.modal.newFieldTitle')}</h3>
            <div className="form-group">
              <label>{t('fieldMap.modal.fieldName')}</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t('fieldMap.modal.namePlaceholder')} />
            </div>
            <div className="form-group">
              <label>{t('fieldMap.modal.cropType')}</label>
              <input type="text" value={formData.cropType} onChange={(e) => setFormData({ ...formData, cropType: e.target.value })} placeholder={t('fieldMap.modal.cropPlaceholder')} />
            </div>
            <div className="form-group">
              <label>{t('fieldMap.modal.plantingDate')}</label>
              <input
                type="date"
                value={formData.plantingDate || new Date().toISOString().split('T')[0]}
                onChange={(e) => setFormData({ ...formData, plantingDate: e.target.value })}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div className="form-group">
                <label>Elevation (m)</label>
                <input
                  type="number"
                  min="0" max="5000" step="1"
                  value={formData.elevation || ''}
                  onChange={(e) => setFormData({ ...formData, elevation: e.target.value })}
                  placeholder="e.g. 350"
                />
              </div>
              <div className="form-group">
                <label>Kc (Crop Coeff.)</label>
                <input
                  type="number"
                  min="0.1" max="2.0" step="0.01"
                  value={formData.kc || ''}
                  onChange={(e) => setFormData({ ...formData, kc: e.target.value })}
                  placeholder="e.g. 1.15"
                />
              </div>
            </div>
            {isAdmin && (
              <div className="form-group">
                <label>{t('fieldMap.modal.assignUser')}</label>
                <select
                  value={formData.userId || ''}
                  onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value="">{t('fieldMap.modal.assignSelf')}</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => { setIsModalOpen(false); if (drawnItemsRef.current) drawnItemsRef.current.clearLayers(); }}>{t('fieldMap.modal.cancel')}</button>
              <button className="btn-save" onClick={() => {
                try {
                  if (!formData.name) { alert('Lütfen bir isim girin'); return; }

                  const fieldData = {
                    ...pendingField,
                    name: formData.name,
                    cropType: formData.cropType,
                    plantingDate: formData.plantingDate ? new Date(formData.plantingDate).toISOString() : new Date().toISOString(),
                    elevation: formData.elevation !== '' && formData.elevation != null ? parseFloat(formData.elevation) : null,
                    kc: formData.kc !== '' && formData.kc != null ? parseFloat(formData.kc) : null
                  };

                  if (formData.userId) {
                    fieldData.userId = formData.userId;
                  }

                  if (onFieldCreateRef.current) {
                    onFieldCreateRef.current(fieldData);
                  } else {
                    console.error('onFieldCreate function is missing!');
                    alert('Hata: Kayıt fonksiyonu bulunamadı.');
                  }

                  setIsModalOpen(false);
                } catch (error) {
                  console.error('Save error:', error);
                  alert('Kaydetme hatası: ' + error.message);
                }
              }}>{t('fieldMap.modal.save')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal — admin only */}
      {showEditModal && isAdmin && (
        <div className="field-modal-overlay">
          <div className="field-modal">
            <h3>{t('fieldMap.modal.editFieldTitle')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div className="form-group">
                <label>Elevation (m)</label>
                <input
                  type="number"
                  min="0" max="5000" step="1"
                  value={editFormData.elevation}
                  onChange={(e) => setEditFormData({ ...editFormData, elevation: e.target.value })}
                  placeholder="e.g. 350"
                />
              </div>
              <div className="form-group">
                <label>Kc (Crop Coeff.)</label>
                <input
                  type="number"
                  min="0.1" max="2.0" step="0.01"
                  value={editFormData.kc}
                  onChange={(e) => setEditFormData({ ...editFormData, kc: e.target.value })}
                  placeholder="e.g. 1.15"
                />
              </div>
            </div>
            <div className="form-group">
              <label>{t('fieldMap.modal.fieldName')}</label>
              <input
                type="text"
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>{t('fieldMap.modal.cropType')}</label>
              <input
                type="text"
                value={editFormData.cropType}
                onChange={(e) => setEditFormData({ ...editFormData, cropType: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>{t('fieldMap.modal.plantingDate')}</label>
              <input
                type="date"
                value={editFormData.plantingDate || ''}
                onChange={(e) => setEditFormData({ ...editFormData, plantingDate: e.target.value })}
              />
            </div>
            {isAdmin && (
              <div className="form-group">
                <label>{t('fieldMap.modal.fieldResponsible')}</label>
                <select
                  value={editFormData.userId || ''}
                  onChange={(e) => setEditFormData({ ...editFormData, userId: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value="">{t('fieldMap.modal.myself')}</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
            )}
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowEditModal(false)}>{t('fieldMap.modal.close')}</button>
              <button className="btn-save" onClick={handleSaveUpdate}>{t('fieldMap.modal.update')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldMap;